import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FadeIn from '../Animations/FadeIn';
import { Button } from '../Components/Button';
import { Icon } from '../Icons/Icon';
import { ScrollProgress } from '../Components/ScrollProgress';
import { MembershipPlanDto } from '../Types/MembershipPlanTypes';
import { ConsultingPricingPublicDto } from '../DataService/ConsultingPricingService';
import { MembershipPlanService } from '../DataService/MembershipPlanService';
import ConsultingPricingService from '../DataService/ConsultingPricingService';
import './LandingPage.css';

const heroPoints = [
  'Du behöver inte kämpa hårdare, skärpa dig eller börja om ännu en gång.',
  'Ibland handlar hållbar förändring om något helt annat – att förstå vad som faktiskt händer inom dig, varför du hamnar i vissa mönster och vad du behöver för att kunna skapa en vardag som fungerar bättre för dig.',
  'Här får du kunskap, stöd och verktyg för att förstå dig själv på djupet och skapa förändringar som håller – i verkliga livet.',
  'Välkommen precis där du är.',
];

const recognitionItems = [
  'Stress.',
  'Överprestation.',
  'Oro.',
  'Självkritik.',
  'Svårigheter att sätta gränser.',
  'En komplicerad relation till mat eller kroppen.',
  'Eller känslan av att du hela tiden behöver börja om.',
];

const understandingItems = [
  'Hur du mår.',
  'Hur din kropp och ditt nervsystem reagerar.',
  'Hur du hanterar stress.',
  'Vilka krav du ställer på dig själv.',
  'Hur du sätter gränser.',
  'Hur du ser på dig själv.',
  'Vilka relationer och miljöer du befinner dig i.',
  'Och vilka strategier du använder när livet blir mycket.',
];

const rotModelItems = [
  {
    title: 'Nervsystem och återhämtning',
    text: 'Att förstå kroppens signaler och skapa mer trygghet, reglering och återhämtning i vardagen.',
  },
  {
    title: 'Självkänsla och inre trygghet',
    text: 'Att förstå hur du ser på dig själv och skapa en mer hållbar relation till dig själv.',
  },
  {
    title: 'Mönster och strategier',
    text: 'Att upptäcka återkommande mönster och förstå varför du gör det du gör – utan att fastna i självkritik.',
  },
  {
    title: 'Relationen till mat och kropp',
    text: 'Att skapa större förståelse för dina beteenden, behov och den relation du har till mat och kropp.',
  },
  {
    title: 'Stress, krav och gränser',
    text: 'Att förstå vad som driver stressen och träna på att skapa mer utrymme, återhämtning och hållbara gränser.',
  },
];

const growBenefits = [
  'Månadens tema med tydlig riktning',
  'Workbook och reflektionsmaterial',
  'Praktiska övningar',
  'Verktyg för nervsystemsreglering',
  'Reflektion och självobservation',
  'Integrationsuppgifter för hållbar förändring',
  'Tillgång till materialet i din egen takt',
  'En struktur som hjälper dig att fortsätta framåt',
];

const growAreas = [
  'Nervsystem och trygghet',
  'Energi och återhämtning',
  'Mönster och relationer',
  'Självkänsla och kroppsrelation',
  'Hållbara vanor',
  'Livsdesign och balans',
];

const supportCards = [
  {
    title: 'GROW',
    text: [
      'För dig som vill ha ett långsiktigt stöd.',
      'Du vill arbeta med dig själv över tid, få kunskap och verktyg och skapa förändring i en takt som fungerar tillsammans med ditt vanliga liv.',
    ],
    cta: 'Läs mer om GROW',
  },
  {
    title: 'ROT-programmet',
    text: [
      'För dig som vill göra en mer fokuserad och djupgående förändringsresa.',
      'Programmet startar 1–2 gånger per år och har ett begränsat antal platser.',
    ],
    cta: 'Läs mer om ROT-programmet',
  },
  {
    title: 'Enskilda samtal',
    text: [
      'För dig som vill ha personlig vägledning i något som är aktuellt just nu.',
      'Ibland behöver du inte ett helt program. Du behöver ett tryggt rum där du kan sortera, förstå och få perspektiv på det som händer.',
      'Samtalen kan bland annat handla om stress, oro, självkritik, gränssättning, relationer, livsbeslut eller återkommande mönster.',
    ],
    cta: 'Boka ett enskilt samtal',
  },
];

const faqItems = [
  {
    question: 'Hur vet jag om GROW är rätt för mig?',
    answer: 'GROW passar dig som vill arbeta med dig själv över tid och få kunskap, struktur och verktyg som du kan använda i din egen vardag. Det passar både dig som är ny på den här typen av arbete och dig som redan har arbetat mycket med dig själv men vill ha kontinuitet och en tydlig riktning framåt.',
  },
  {
    question: 'Hur skiljer sig GROW från ROT-programmet?',
    answer: 'GROW är ett medlemskap och ditt kontinuerliga stöd. Du arbetar med ett nytt fokus varje månad och kan följa processen i din egen takt. ROT-programmet är en tidsbegränsad och mer intensiv 12-veckors kurs som öppnar 1–2 gånger per år med ett begränsat antal platser. Båda utgår från samma grund – ROT-modellen.',
  },
  {
    question: 'Måste jag gå ROT för att vara med i GROW?',
    answer: 'Nej. GROW står helt på egna ben och du behöver inte ha gått ROT-programmet.',
  },
  {
    question: 'Kan jag börja i GROW och senare gå ROT?',
    answer: 'Ja. Om du längre fram känner att du vill göra en djupare och mer intensiv förändringsresa kan ROT-programmet vara ett naturligt nästa steg när nästa omgång öppnar.',
  },
  {
    question: 'Måste jag ha stora problem för att söka stöd?',
    answer: 'Nej. Du behöver inte vänta tills livet känns ohållbart. Ibland är det just när du märker att något inte fungerar som du vill som det är värdefullt att börja förstå vad som händer.',
  },
  {
    question: 'Är allt online?',
    answer: 'Ja. Alla samtal sker online, vilket gör det möjligt att få stöd oavsett var du bor.',
  },
];

const formatPrice = (value?: number | null) => (typeof value === 'number' ? value.toLocaleString('sv-SE') : '');

export const LandingPage = () => {
  const navigate = useNavigate();
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlanDto[]>([]);
  const [consultingPricing, setConsultingPricing] = useState<ConsultingPricingPublicDto | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const [plans, pricing] = await Promise.all([
        MembershipPlanService.getPublic().catch(() => null),
        ConsultingPricingService.getPublic().catch(() => null),
      ]);

      if (!mounted) return;

      setMembershipPlans(plans ?? []);
      setConsultingPricing(pricing);
    };

    void load();

    return () => {
      mounted = false;
    };
  }, []);

  const basicPlan = membershipPlans.find((plan) => plan.key === 'basic');
  const premiumPlan = membershipPlans.find((plan) => plan.key === 'premium');

  const sessionPrice = consultingPricing?.config.baseSessionPrice;

  const startIntroBooking = () => {
    navigate('/booking', { state: { bookingMode: 'intro' } });
  };

  return (
    <div className="home-root">
      <ScrollProgress />

      <section className="home-hero">
        <div className="home-shell home-hero-shell">
          <FadeIn className="home-hero-stack">
            <div className="home-hero-portrait-wrap">
              <img src="/images/IMG_3996.jpeg" alt="Sandra" className="home-hero-portrait" />
            </div>

            <p className="home-kicker">Sandras Hälsa</p>
            <h1 className="home-hero-title">För dig som vill må bättre – och förstå varför det är så svårt att få förändringen att hålla</h1>
            <div className="home-hero-lead">
              <p>Välkommen till Sandras Hälsa</p>
              {heroPoints.map((point) => (
                <p key={point}>{point}</p>
              ))}
            </div>

          </FadeIn>
        </div>
      </section>

      <section className="home-section home-section-muted">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>GROW – ditt stöd för hållbar förändring över tid</h2>
            <p>GROW är medlemskapet för dig som vill investera i dig själv, förstå dina mönster och steg för steg skapa mer balans, energi och hållbarhet i vardagen.</p>
            <p>Du behöver inte förändra allt på en gång.</p>
            <p>Varje månad får du ett nytt fokus, kunskap att luta dig mot, praktiska övningar och tid att integrera det du lär dig i ditt eget liv.</p>
            <p>GROW passar både dig som precis har börjat utforska vad du behöver och dig som redan har arbetat mycket med dig själv men vill ha kontinuitet, riktning och stöd för att fortsätta framåt.</p>
          </FadeIn>

          <div className="home-hero-actions">
            <Button className="home-button home-button-inline" onClick={() => navigate('/membership')} text="Läs mer om GROW" />
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Du behöver inte börja om. Du kan börja förstå.</h2>
            <p>Kanske känner du igen dig i att du fungerar bra på utsidan, men inuti ofta går på högvarv.</p>
            <p>Du tar ansvar. Tänker mycket. Försöker få ihop vardagen. Kanske ställer du höga krav på dig själv och vet egentligen ganska väl vad du "borde" göra.</p>
            <p>Ändå hamnar du ibland tillbaka i samma mönster.</p>
          </FadeIn>

          <div className="home-list-grid">
            {recognitionItems.map((item) => (
              <FadeIn key={item} className="home-list-item">
                <span className="home-list-dot" />
                <p>{item}</p>
              </FadeIn>
            ))}
          </div>

          <div style={{ marginTop: '1.75rem' }}>
            <FadeIn className="home-section-heading">
              <p>Det betyder inte att du saknar disciplin.</p>
              <p>Och det betyder inte att det är något fel på dig.</p>
              <p>Ofta finns det en historia bakom våra sätt att tänka, känna och agera. Strategier som en gång hjälpte oss att hantera livet kan fortsätta att styra oss långt efter att vi inte längre behöver dem på samma sätt.</p>
              <p>När vi börjar förstå vad som händer blir det också lättare att förändra.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="home-section home-section-muted">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Hållbar förändring börjar med förståelse</h2>
            <p>Jag tror inte på att pressa fram förändring genom ännu fler regler, krav och måsten.</p>
            <p>Jag tror på att förstå helheten.</p>
          </FadeIn>

          <div className="home-card-grid home-card-grid-tight">
            {understandingItems.map((item) => (
              <FadeIn key={item} className="home-info-card">
                <p>{item}</p>
              </FadeIn>
            ))}
          </div>

          <div style={{ marginTop: '1.75rem' }}>
            <FadeIn className="home-section-heading">
              <p>När vi ser sambanden blir det lättare att veta vad som faktiskt behöver förändras.</p>
              <p>Och framför allt – vad som är möjligt att förändra på ett sätt som fungerar även när vardagen inte är perfekt.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="home-section home-rot-section">
        <div className="home-shell">
          <FadeIn className="home-section-heading home-section-heading-light">
            <div className="home-rot-kicker">ROT-modellen</div>
            <h2>Grunden i mitt sätt att arbeta</h2>
            <p>Oavsett om du möter mig genom GROW, en kurs eller ett individuellt samtal utgår jag från samma grundsyn: hållbar förändring behöver börja på djupet.</p>
            <p>Därför arbetar jag utifrån ROT-modellen.</p>
            <p>ROT hjälper oss att se bakom det som syns på ytan och förstå hur olika delar av livet hänger samman.</p>
            <p>Vi tittar bland annat på:</p>
          </FadeIn>

          <FadeIn className="home-rot-copy">
            <ul>
              {rotModelItems.map((item) => (
                <li key={item.title}>
                  <span>
                    <strong>{item.title}</strong>
                    <br />
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
            
            <p className="home-section-label-light home-rot-subtitle">ROT-modellen är alltså inte ett program du måste gå.</p>
            <p className="home-section-label-light">Det är grunden i hur jag arbetar.</p>
          </FadeIn>

          <FadeIn className="home-rot-panel">
            <div className="home-rot-image-wrap">
              <img src="/images/rot.png" alt="ROT" className="home-rot-image" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="home-section">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>GROW</h2>
            <p>Väx i din egen takt</p>
            <p>GROW är för dig som vill göra en hållbar förändring utan att behöva vända upp och ner på hela livet på en gång.</p>
            <p>Här får du möjlighet att arbeta med dig själv över tid, med ett nytt fokus varje månad.</p>
            <p>Du får kunskap, reflektion, praktiska verktyg och möjlighet att steg för steg omsätta det du lär dig i din egen vardag.</p>
            <p>I GROW får du bland annat:</p>
          </FadeIn>

          <div className="home-list-grid">
            {growBenefits.map((item) => (
              <FadeIn key={item} className="home-list-item">
                <span className="home-list-dot" />
                <p>{item}</p>
              </FadeIn>
            ))}
          </div>

          <div className="home-grow-card">
            <div className="home-grow-grid">
              <FadeIn className="home-grow-panel">
                <p className="home-section-label">GROW Basic</p>
                {basicPlan?.monthlyPrice != null && <div className="home-grow-price">{formatPrice(basicPlan.monthlyPrice)} kr/månad</div>}
                <p>För dig som vill ha kunskap, struktur och verktyg att arbeta med på egen hand.</p>
                <p>Du får tillgång till GROW:s månadsinnehåll och kan följa resan i din egen takt.</p>
                <p>I GROW arbetar vi bland annat med:</p>
                <ul>
                  {growAreas.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Button className="home-button home-button-inline" variant="secondary" onClick={() => navigate('/membership')} text="Jag vill börja med GROW" />
              </FadeIn>

              <FadeIn className="home-grow-panel home-grow-panel-accent">
                <p className="home-section-label">GROW Premium</p>
                {premiumPlan?.monthlyPrice != null && <div className="home-grow-price">{formatPrice(premiumPlan.monthlyPrice)} kr/månad</div>}
                <p>För dig som vill ha samma grund som i GROW Basic – men också kontinuerlig personlig vägledning.</p>
                <p>Du får allt som ingår i Basic samt:</p>
                <ul>
                  <li>Månatligt 1:1-samtal online</li>
                  <li>Individuell guidning utifrån där du befinner dig</li>
                  <li>Möjlighet att få stöd att omsätta kunskapen i din egen vardag</li>
                </ul>
                <Button className="home-button home-button-inline" variant="secondary" onClick={() => navigate('/membership')} text="Jag vill ha GROW Premium" />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-section-muted">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Du behöver inte göra resan ensam</h2>
            <p>Förändring sker inte alltid linjärt.</p>
            <p>Ibland känns det som att du gör stora framsteg. Ibland kommer livet emellan. Gamla mönster gör sig påminda och du tappar riktningen.</p>
            <p>Det är en del av processen.</p>
            <p>GROW är skapat för att ge dig en plats att återvända till.</p>
            <p>Att fortsätta lära känna dig själv.</p>
            <p>Att stanna upp.</p>
            <p>Reflektera.</p>
            <p>Justera.</p>
            <p>Prova igen.</p>
            <p>Inte för att du har misslyckats – utan för att hållbar förändring får ta tid.</p>
          </FadeIn>
        </div>
      </section>

      <section className="home-section">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>ROT-programmet</h2>
            <p>För dig som vill gå djupare</p>
            <p>GROW är kärnan i Sandras Hälsa och finns för dig som vill ha ett långsiktigt stöd i din utveckling.</p>
            <p>Men ibland vill man göra en mer fokuserad och intensiv förändringsresa.</p>
            <p>Då finns ROT-programmet.</p>
            <p>ROT är min fördjupande 12-veckors kurs där vi arbetar mer strukturerat och intensivt utifrån ROT-modellen.</p>
            <p>Under programmet får du möjlighet att gå djupare i dina mönster, förstå vad som håller dem vid liv och steg för steg skapa nya sätt att möta dig själv och din vardag.</p>
            <p>ROT-programmet öppnar endast 1–2 gånger per år</p>
            <p>Jag tar in ett begränsat antal deltagare vid varje kursstart.</p>
            <p>Det gör att jag kan ge deltagarna den vägledning och närvaro som krävs för en djupare process.</p>
            <p>Vill du få information när nästa omgång öppnar?</p>
          </FadeIn>

          <div className="home-hero-actions">
            <Button className="home-button home-button-inline" onClick={() => navigate('/rot/signup')} text="Anmäl dig till intresselistan" />
          </div>
        </div>
      </section>

      <section className="home-section home-section-muted">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Vilket stöd passar dig?</h2>
          </FadeIn>

          <div className="home-card-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {supportCards.map((card) => (
              <FadeIn key={card.title} className="home-mini-card">
                <h3>{card.title}</h3>
                {card.text.map((item) => (
                  <p key={item}>{item}</p>
                ))}
                {card.title === 'Enskilda samtal' && sessionPrice != null && <p>60 minuter online – {formatPrice(sessionPrice)} kr</p>}
                <Button
                  className="home-button home-button-inline"
                  variant="secondary"
                  onClick={() => {
                    if (card.title === 'GROW') {
                      navigate('/membership');
                      return;
                    }

                    if (card.title === 'ROT-programmet') {
                      navigate('/rot/signup');
                      return;
                    }

                    navigate('/booking');
                  }}
                  text={card.cta}
                />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Du behöver inte vara "tillräckligt dålig" för att få stöd</h2>
            <p>Du behöver inte vänta tills allt känns övermäktigt.</p>
            <p>Du behöver inte ha stora problem.</p>
            <p>Och du behöver inte veta exakt vad du behöver hjälp med innan du börjar.</p>
            <p>Kanske fungerar livet ganska bra på många områden, men du känner ändå att något skaver.</p>
            <p>Kanske är du trött på att förstå vad du borde göra men inte lyckas få förändringen att hålla.</p>
            <p>Eller så längtar du helt enkelt efter mer energi, balans, trygghet och kontakt med dig själv.</p>
            <p>Det räcker.</p>
            <p>Du får börja precis där du är.</p>
          </FadeIn>
        </div>
      </section>

      <section className="home-section home-faq-section">
        <div className="home-shell home-narrow">
          <FadeIn className="home-section-heading">
            <h2>Vanliga frågor</h2>
          </FadeIn>

          <div className="home-faq-grid">
            {faqItems.map((item) => (
              <FadeIn key={item.question} className="home-faq-card">
                <div className="home-faq-question">
                  <Icon.CheckMarkCircle color="#7C444F" />
                  <h3>{item.question}</h3>
                </div>
                <p>{item.answer}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-faq-section">
        <div className="home-shell home-final-cta-shell">
          <FadeIn className="home-final-cta">
            <img src="/images/skog.png" alt="Trygghet" className="home-final-cta-image" />
            <div className="home-final-cta-copy">
              <p className="home-section-label home-section-label-light">Kanske är det dags att sluta börja om</p>
              <h2>Du behöver inte bli en ny version av dig själv.</h2>
              <p>Du behöver inte göra allting rätt.</p>
              <p>Och du behöver inte kämpa hårdare.</p>
              <p>Kanske börjar förändringen istället med att du stannar upp och blir nyfiken på dig själv.</p>
              <p>Förstår vad du behöver.</p>
              <p>Ser dina mönster med lite mer värme.</p>
              <p>Och börjar skapa en vardag som faktiskt fungerar för dig.</p>
              <p>Du är varmt välkommen till Sandras Hälsa.</p>
              <p>Vill du börja med GROW?</p>
              <div className="home-final-actions">
                <Button className="home-button home-button-contrast" size="lg" onClick={() => navigate('/membership')} text="Läs mer om GROW" />
              </div>
              <p>Vill du prata med mig först?</p>
              <div className="home-final-actions">
                <Button className="home-button home-button-ghost" size="lg" variant="secondary" onClick={startIntroBooking} text="Boka ett kostnadsfritt introduktionssamtal" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};
